import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form, ProgressBar, Row, Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport, faMoneyBillTransfer, faRotateLeft, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

const SHEET_GROUP = "tiktok_lising_sheets";
const STORAGE_KEY = "paypal_tools_selected_sheet";

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthsAgoIso = (m) => {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
};

const initialProgress = () => ({
  status: "idle", // idle | running | done | error
  message: "",
  current: 0,
  total: 0,
  appended: 0,
  sheetName: "",
  error: "",
});

const PaypalTools = ({ accountList }) => {
  const [sheetsList, setSheetsList] = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [loadingSheets, setLoadingSheets] = useState(true);

  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState(monthsAgoIso(1));
  const [endDate, setEndDate] = useState(todayIso());

  const [running, setRunning] = useState(""); // 'payments' | 'disputes' | 'refunds'
  const [progress, setProgress] = useState({
    payments: initialProgress(),
    disputes: initialProgress(),
    refunds: initialProgress(),
  });
  const cancelRef = useRef(false);

  useEffect(() => {
    const fetchSheets = async () => {
      try {
        const response = await Utils._apiRequest({
          path: Enums.endpoints.options.LIST,
          type: "GET",
        });
        const options = response.data || [];
        const list = options.filter((o) => o.group === SHEET_GROUP);
        setSheetsList(list);
        if (selectedSheetId && !list.some((s) => s.key === selectedSheetId)) {
          setSelectedSheetId("");
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.log("fetch sheets:", e.message);
      } finally {
        setLoadingSheets(false);
      }
    };
    fetchSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!accountId && accountList?.length) {
      setAccountId(accountList[0]._id);
    }
  }, [accountList, accountId]);

  const handleSheetChange = (e) => {
    const value = e.target.value;
    setSelectedSheetId(value);
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const updateProgress = (kind, patch) => {
    setProgress((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
  };

  const validateInputs = () => {
    if (!accountId) { toast.warning("Chọn tài khoản PayPal trước"); return false; }
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet để lưu kết quả"); return false; }
    if (!startDate || !endDate) { toast.warning("Chọn khoảng ngày"); return false; }
    return true;
  };

  /* -------------------- PAYMENTS -------------------- */
  const runPayments = async () => {
    if (!validateInputs()) return;
    const kind = "payments";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      const init = await Utils._apiRequest({
        path: Enums.endpoints.paypal.EXPORT_PAYMENTS_INIT,
        type: "POST",
        payload: { accountId, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate },
      });
      if (!init?.ok) throw new Error(init?.errors || "Init thất bại");

      const windows = init.windows || [];
      const seen = Array.isArray(init.existingKeys) ? [...init.existingKeys] : [];
      updateProgress(kind, {
        total: windows.length,
        sheetName: init.sheetName,
        message: `0/${windows.length} cửa sổ • ${seen.length} dòng đã có`,
      });

      let appended = 0;
      for (let i = 0; i < windows.length; i++) {
        if (cancelRef.current) break;
        const w = windows[i];
        // eslint-disable-next-line no-await-in-loop
        const r = await Utils._apiRequest({
          path: Enums.endpoints.paypal.EXPORT_PAYMENTS_CHUNK,
          type: "POST",
          payload: {
            accountId,
            sheetId: selectedSheetId,
            sheetName: init.sheetName,
            headers: init.headers,
            windowStart: w.start,
            windowEnd: w.end,
            existingKeys: seen,
            transactionType: "T0006",
          },
        });
        if (!r?.ok) throw new Error(r?.errors || "Chunk thất bại");
        appended += r.count || 0;
        if (Array.isArray(r.addedKeys)) seen.push(...r.addedKeys);
        updateProgress(kind, {
          current: i + 1,
          appended,
          message: `${i + 1}/${windows.length} cửa sổ — thêm ${appended} dòng mới`,
        });
      }
      updateProgress(kind, { status: "done", message: `Hoàn tất • thêm ${appended} dòng mới` });
      toast.success(`Payments: thêm ${appended} dòng mới → ${init.sheetName}`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  /* -------------------- DISPUTES -------------------- */
  const runDisputes = async () => {
    if (!validateInputs()) return;
    const kind = "disputes";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      const init = await Utils._apiRequest({
        path: Enums.endpoints.paypal.EXPORT_DISPUTES_INIT,
        type: "POST",
        payload: { accountId, sheetId: selectedSheetId, sheetName: "Disputes", startDate, endDate },
      });
      if (!init?.ok) throw new Error(init?.errors || "Init thất bại");
      const existingMap = { ...(init.existingMap || {}) };
      updateProgress(kind, {
        sheetName: init.sheetName,
        message: `Đang tải trang đầu… (${Object.keys(existingMap).length} dòng đã có)`,
      });

      let nextPageToken = null;
      let pageIndex = 0;
      let totalAppended = 0;
      let totalUpdated = 0;
      do {
        if (cancelRef.current) break;
        // eslint-disable-next-line no-await-in-loop
        const r = await Utils._apiRequest({
          path: Enums.endpoints.paypal.EXPORT_DISPUTES_CHUNK,
          type: "POST",
          payload: {
            accountId,
            sheetId: selectedSheetId,
            sheetName: init.sheetName,
            headers: init.headers,
            startTime: init.startTime,
            endTime: init.endTime,
            nextPageToken,
            existingMap,
          },
        });
        if (!r?.ok) throw new Error(r?.errors || "Chunk thất bại");
        pageIndex += 1;
        totalAppended += r.appended || 0;
        totalUpdated += r.updated || 0;
        if (r.addedMap) Object.assign(existingMap, r.addedMap);
        nextPageToken = r.nextPageToken || null;
        updateProgress(kind, {
          current: pageIndex,
          total: pageIndex + (nextPageToken ? 1 : 0),
          appended: totalAppended,
          message: nextPageToken
            ? `Trang ${pageIndex} • +${totalAppended} mới / ${totalUpdated} cập nhật — đang tải tiếp…`
            : `Trang ${pageIndex} • +${totalAppended} mới / ${totalUpdated} cập nhật`,
        });
      } while (nextPageToken);

      updateProgress(kind, {
        status: "done",
        total: pageIndex,
        message: `Hoàn tất • +${totalAppended} mới / ${totalUpdated} cập nhật`,
      });
      toast.success(`Disputes: +${totalAppended} mới, ${totalUpdated} cập nhật → ${init.sheetName}`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  /* -------------------- REFUNDS -------------------- */
  const runRefunds = async () => {
    if (!validateInputs()) return;
    const kind = "refunds";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      const init = await Utils._apiRequest({
        path: Enums.endpoints.paypal.EXPORT_REFUNDS_INIT,
        type: "POST",
        payload: { accountId, sheetId: selectedSheetId, sheetName: "Refunded", startDate, endDate },
      });
      if (!init?.ok) throw new Error(init?.errors || "Init thất bại");

      const windows = init.windows || [];
      const seen = Array.isArray(init.existingKeys) ? [...init.existingKeys] : [];
      updateProgress(kind, {
        total: windows.length,
        sheetName: init.sheetName,
        message: `0/${windows.length} cửa sổ • ${seen.length} dòng đã có`,
      });

      let appended = 0;
      for (let i = 0; i < windows.length; i++) {
        if (cancelRef.current) break;
        const w = windows[i];
        // eslint-disable-next-line no-await-in-loop
        const r = await Utils._apiRequest({
          path: Enums.endpoints.paypal.EXPORT_REFUNDS_CHUNK,
          type: "POST",
          payload: {
            accountId,
            sheetId: selectedSheetId,
            sheetName: init.sheetName,
            headers: init.headers,
            windowStart: w.start,
            windowEnd: w.end,
            existingKeys: seen,
            transactionTypes: ["T1107", "T1106"],
          },
        });
        if (!r?.ok) throw new Error(r?.errors || "Chunk thất bại");
        appended += r.count || 0;
        if (Array.isArray(r.addedKeys)) seen.push(...r.addedKeys);
        updateProgress(kind, {
          current: i + 1,
          appended,
          message: `${i + 1}/${windows.length} cửa sổ — thêm ${appended} dòng mới`,
        });
      }
      updateProgress(kind, { status: "done", message: `Hoàn tất • thêm ${appended} dòng mới` });
      toast.success(`Refunds: thêm ${appended} dòng mới → ${init.sheetName}`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  const renderProgress = (kind) => {
    const p = progress[kind];
    if (p.status === "idle") return null;

    const variant = p.status === "error" ? "danger" : p.status === "done" ? "success" : "info";
    const pct = p.total > 0
      ? Math.round((p.current / p.total) * 100)
      : (p.status === "running" ? 100 : 0);

    return (
      <div className="mt-2">
        <ProgressBar
          now={pct}
          striped={p.status === "running"}
          animated={p.status === "running"}
          variant={variant}
          label={p.total > 0 ? `${p.current}/${p.total}` : ""}
          style={{ height: 14 }}
        />
        <div className="small mt-1">
          {p.status === "error" ? (
            <Badge bg="danger">Lỗi: {p.error}</Badge>
          ) : (
            <span className={p.status === "done" ? "text-success" : "text-muted"}>
              {p.message}
              {p.sheetName && p.status === "done" && (
                <Badge bg="success" className="ms-2">→ {p.sheetName}</Badge>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderRunButton = (kind, label, variant, onClick) => {
    const isRunning = running === kind;
    return (
      <Button size="sm" variant={variant} disabled={!!running} onClick={onClick}>
        {isRunning ? (
          <><Spinner size="sm" animation="border" /> Đang chạy…</>
        ) : label}
      </Button>
    );
  };

  return (
    <div className="my-3">
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileExport} className="me-2" />
          <strong>Công cụ Export</strong>
        </Card.Header>
        <Card.Body>
          {accountList?.length === 0 && (
            <Alert variant="warning" className="py-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              Chưa có tài khoản PayPal nào. Hãy thêm tài khoản ở phía trên trước.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Tài khoản PayPal</Form.Label>
                <Form.Select
                  size="sm"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={!accountList?.length || !!running}
                >
                  {(accountList || []).map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.accountName} ({a.meta?.mode || "live"})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Từ ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={!!running}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Đến ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={!!running}
                />
              </Form.Group>
            </Col>
            <Col md={2}></Col>

            <Col md={9}>
              <Form.Group>
                <Form.Label className="small">Google Sheet lưu kết quả</Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedSheetId}
                  onChange={handleSheetChange}
                  disabled={loadingSheets || !!running}
                >
                  <option value="">-- Chọn sheet đã lưu --</option>
                  {sheetsList.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.values?.name || s.key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                as={Link}
                to="/settings#google-sheet"
                variant="dark"
                size="sm"
                className="w-100"
              >
                <FontAwesomeIcon icon={faGoogleDrive} className="me-2" />
                Quản lý Sheets
              </Button>
            </Col>
          </Row>

          <hr />

          <Row className="g-3">
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faMoneyBillTransfer} className="me-2 text-primary" />
                    Export Payments
                  </h6>
                  <p className="small text-muted mb-2">
                    Transactions type T0006 → tab <code>Payments</code>. Chia theo cửa sổ 30 ngày.
                  </p>
                  {renderRunButton("payments", "Chạy Export Payments", "primary", runPayments)}
                  {renderProgress("payments")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-2 text-warning" />
                    Export Disputes
                  </h6>
                  <p className="small text-muted mb-2">
                    Disputes → tab <code>Disputes</code>. Tải theo trang (page_size=20).
                  </p>
                  {renderRunButton("disputes", "Chạy Export Disputes", "warning", runDisputes)}
                  {renderProgress("disputes")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faRotateLeft} className="me-2 text-danger" />
                    Export Refunds
                  </h6>
                  <p className="small text-muted mb-2">
                    Refund thường (T1107) + refund do thua dispute / chargeback (T1106), unique theo <code>transaction_id</code> → tab <code>Refunded</code>.
                  </p>
                  {renderRunButton("refunds", "Chạy Export Refunds", "danger", runRefunds)}
                  {renderProgress("refunds")}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaypalTools;
